import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { useDeepLinkFocus } from "@/components/ui/deep-link-focus/useDeepLinkFocus";
import { EntityLink } from "../shared/EntityLink";
import type { ScopeLabelCache } from "../audits/useScopeLabelCache";
import { useMissionConversation } from "./useMissionConversation";
import { TaskConversationBlock } from "./TaskConversationBlock";
import { GuardianEvalBubble } from "./GuardianEvalBubble";

export interface ConversationViewProps {
  missionId?: string | null;
  taskId?: string | null;
  focus?: string | null;
  scopeLabels: ScopeLabelCache;
  onBack?: () => void;
  variant?: "embedded" | "window";
}

export function ConversationView({
  missionId,
  taskId,
  focus,
  scopeLabels,
  onBack,
  variant = "embedded",
}: ConversationViewProps) {
  const { data, loading, error } = useMissionConversation({ missionId, taskId });
  const isWindow = variant === "window";
  useDeepLinkFocus(loading || !data ? null : focus);

  return (
    <div
      className={clsx(
        "flex h-full flex-col overflow-hidden",
        isWindow ? "bg-slate-900/40 text-slate-100" : null
      )}
      data-testid="conversation-view"
    >
      {!isWindow ? (
        <header className="shrink-0 border-b border-white/5 px-6 py-4">
          <div className="flex items-center gap-3">
            {onBack ? (
              <button
                type="button"
                onClick={onBack}
                className="rounded-lg p-2 text-slate-400 hover:bg-white/5 hover:text-slate-200"
                aria-label="Back to alerts"
                data-testid="conversation-back"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
              </button>
            ) : null}
            <div className="min-w-0 flex-1 text-center">
              {data?.mission ? (
                <EntityLink
                  subject={{ type: "mission", id: data.mission.id }}
                  scope={{ mission_id: data.mission.id }}
                  scopeLabels={scopeLabels}
                  title={data.mission.name}
                  className="text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-200"
                />
              ) : taskId ? (
                <EntityLink
                  subject={{ type: "task", id: taskId }}
                  scope={{ task_id: taskId }}
                  scopeLabels={scopeLabels}
                  className="text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-200"
                />
              ) : (
                <h2 className="text-lg font-semibold text-slate-100">Conversation</h2>
              )}
            </div>
          </div>
        </header>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-2 sm:px-5">
        <div className="mx-auto w-full max-w-xl">
          {loading ? <p className="text-sm text-slate-400">Loading conversation…</p> : null}
          {error ? <p className="text-sm text-rose-300">{error}</p> : null}
          {data ? (
            <div className="space-y-3">
              {data.mission && isWindow ? (
                <div className="text-center" data-testid="conversation-mission-header">
                  <EntityLink
                    subject={{ type: "mission", id: data.mission.id }}
                    scope={{ mission_id: data.mission.id }}
                    scopeLabels={scopeLabels}
                    title={data.mission.name}
                    className="text-xs font-semibold uppercase tracking-wide text-slate-400 hover:text-slate-200"
                  />
                </div>
              ) : null}
              {data.tasks.map((row) => (
                <TaskConversationBlock
                  key={row.task.id}
                  row={row}
                  missionId={data.mission_id}
                  scopeLabels={scopeLabels}
                />
              ))}
              {data.mission_screening ? (
                <GuardianEvalBubble
                  screening={data.mission_screening}
                  scopeLabels={scopeLabels}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
